import React, { useState, useEffect } from 'react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Label } from '../../components/Label';
import { Select } from '../../components/Select';
import api from '../../services/api';
import { toast } from 'sonner';

interface Pedido {
  modelo: string;
  material?: string;
  quantidade: number;
  preco_unidade: number;
  preco_total_pedido: number;
  cor: string;
  observacao?: string;
}

interface MenuNovaOrdemServicoProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const MenuNovaOrdemServico: React.FC<MenuNovaOrdemServicoProps> = ({ onClose, onSuccess }) => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [showAddPedido, setShowAddPedido] = useState(false);
  const [modelos, setModelos] = useState<{ id: number; nome: string; material: number }[]>([]);
  const [loadingModelos, setLoadingModelos] = useState(true);

  // Campos do pedido em edição
  const [modelo, setModelo] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [precoUnidade, setPrecoUnidade] = useState(0);
  const [cor, setCor] = useState('');
  const [observacao, setObservacao] = useState('');

  useEffect(() => {
    const fetchModelos = async () => {
      setLoadingModelos(true);
      try {
        const res = await api.get('/api/v1/modelos/');
        setModelos(res.data);
      } catch {
        setModelos([]);
      } finally {
        setLoadingModelos(false);
      }
    };
    fetchModelos();
  }, []);

  const handleAddPedido = () => {
    if (!modelo || quantidade <= 0 || precoUnidade <= 0 || !cor) {
      toast.error('Preencha todos os campos do pedido');
      return;
    }
    // Busca o modelo selecionado para obter o id do material
    const modeloSelecionado = modelos.find(m => m.id + '' === modelo);
    if (!modeloSelecionado) {
      toast.error('Modelo selecionado inválido ou sem material vinculado');
      return;
    }
    setPedidos(prev => [
      ...prev,
      {
        modelo,
        material: modeloSelecionado.material + '', // id do material como string
        quantidade,
        preco_unidade: precoUnidade,
        preco_total_pedido: quantidade * precoUnidade,
        cor,
        observacao: observacao?.trim() || undefined,
      }
    ]);
    setModelo('');
    setQuantidade(1);
    setPrecoUnidade(0);
    setCor('');
    setObservacao('');
    setShowAddPedido(false);
  };

  const handleRemovePedido = (idx: number) => {
    setPedidos(pedidos.filter((_, i) => i !== idx));
  };

  const totalOrdem = pedidos.reduce((acc, p) => acc + p.preco_total_pedido, 0);

  // Adiciona prop para forçar atualização após criar ordem
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pedidos.length === 0) {
      toast.error('Adicione pelo menos um pedido');
      return;
    }
    try {
      await api.post('/api/v1/ordens_servico/', {
        total: totalOrdem,
        status: 'pendente', // força sempre pendente
          pedidos: pedidos.map(p => ({
          modelo: Number(p.modelo),
          quantidade: p.quantidade,
          preco_unidade: p.preco_unidade,
          preco_total_pedido: p.preco_total_pedido,
            status: 'pendente', // sempre pendente
            cor: p.cor,
            ...(p.observacao ? { observacao: p.observacao } : {})
        }))
      });
      toast.success('Ordem de serviço criada!');
      onSuccess();
      onClose();
    } catch {
      toast.error('Erro ao criar ordem de serviço');
    }
  };

  return (
    <div className="menu-novo-material-overlay">
      <div className="menu-novo-material" style={{minWidth: 400, maxWidth: 600}}>
        <h3 className="menu-novo-material-title">Nova Ordem de Serviço</h3>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: 16}}>
            <b>Pedidos adicionados:</b>
            {pedidos.length === 0 && <div style={{color:'#888', fontSize:14}}>Nenhum pedido adicionado</div>}
            <ul style={{paddingLeft: 18}}>
              {pedidos.map((p, idx) => (
                <li key={idx} style={{marginBottom: 4}}>
                  {p.quantidade}x {modelos.find(m=>m.id+''===p.modelo)?.nome || 'Modelo'} - Cor: {p.cor} - R$ {p.preco_unidade} un. (Total: R$ {p.preco_total_pedido}){p.observacao ? ` — ${p.observacao}` : ''}
                  <Button size="sm" variant="destructive" style={{marginLeft:8}} type="button" onClick={()=>handleRemovePedido(idx)}>Remover</Button>
                </li>
              ))}
            </ul>
          </div>
          {showAddPedido ? (
            <div style={{border:'1px solid #eee', borderRadius:8, padding:12, marginBottom:16}}>
              <Label>Modelo</Label>
              <Select
                value={modelo}
                onChange={e => setModelo(e.target.value)}
                options={modelos.map(m => ({ value: m.id, label: m.nome }))}
                emptyMessage={loadingModelos ? 'Carregando modelos...' : 'Nenhum modelo cadastrado'}
                disabled={loadingModelos}
              />
              <Label>Quantidade</Label>
              <Input type="number" min={1} value={quantidade} onChange={e => setQuantidade(Number(e.target.value))} />
              <Label>Preço unitário</Label>
              <Input type="number" min={0} value={precoUnidade} onChange={e => setPrecoUnidade(Number(e.target.value))} />
              <Label>Cor (obrigatório)</Label>
              <Input type="text" value={cor} onChange={e => setCor(e.target.value)} placeholder="Ex: Preto, Azul..." />
              <Label>Observação (opcional)</Label>
              <Input type="text" value={observacao} onChange={e => setObservacao(e.target.value)} placeholder="Detalhes adicionais" />
              <div style={{margin:'8px 0', fontWeight:500}}>
                Preço total: R$ {quantidade * precoUnidade}
              </div>
              <div className="menu-novo-material-actions">
                <Button type="button" variant="secondary" onClick={()=>setShowAddPedido(false)}>Cancelar</Button>
                <Button type="button" onClick={handleAddPedido}>Adicionar Pedido</Button>
              </div>
            </div>
          ) : (
            <Button type="button" onClick={()=>setShowAddPedido(true)} style={{marginBottom:16}}>
              Adicionar Pedido
            </Button>
          )}
          <div style={{margin:'12px 0', fontWeight:600}}>
            Total da ordem: R$ {totalOrdem}
          </div>
          <div className="menu-novo-material-actions">
            <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button type="submit">Criar Ordem de Serviço</Button>
          </div>
        </form>
      </div>
    </div>
  );
};
